import type { AccessSystem, MappingDecideBody, MappingItem, MatchHint } from '../../api/types'
import { attachTarget, isMergeTarget } from './MappingAttachPicker'
import type { AttachTargetKind } from './MappingAttachPicker'
import { createAtFields, createSeriesAtFields } from './MappingCreateForm'
import { isSightedCandidate } from './MappingWorkbench'

/** A suggestion score at the backend's rounded 100% (exact hits are 1.0) —
 *  the confidence signal every quick action keys on. */
const EXACT_SCORE = 0.9995

/** 100% ATTACH-TARGET hints (target_kind null/absent); empty for sighted
 *  rows (their convert needs a holder person) and attach-less kinds. */
export function exactAttachHints(system: AccessSystem, item: MappingItem): MatchHint[] {
  if (isSightedCandidate(item) || attachTarget(system.provider, item.kind) === null) return []
  return item.suggestions.filter(
    (s) => (s.target_kind === null || s.target_kind === undefined) && s.score >= EXACT_SCORE,
  )
}

/** 100% create-AT location hints (createAtFields must accept the row —
 *  rotation-detected door_codes are excluded there). */
export function exactLocationHints(system: AccessSystem, item: MappingItem): MatchHint[] {
  if (isSightedCandidate(item)) return []
  return item.suggestions.filter(
    (s) =>
      s.target_kind === 'location' &&
      s.score >= EXACT_SCORE &&
      createAtFields(system, item, s.canonical_uid) !== null,
  )
}

/** 100% rotation-FAMILY hints (target_kind 'door_code_series') — the
 *  candidate matches an EXISTING series. */
export function exactSeriesAddHints(item: MappingItem): MatchHint[] {
  if (isSightedCandidate(item)) return []
  return item.suggestions.filter((s) => s.target_kind === 'door_code_series' && s.score >= EXACT_SCORE)
}

/** 100% create-SERIES-at location hints — only when no family hint exists
 *  (an existing family match wins). */
export function exactSeriesAtHints(item: MappingItem): MatchHint[] {
  if (isSightedCandidate(item) || exactSeriesAddHints(item).length > 0) return []
  return item.suggestions.filter(
    (s) =>
      s.target_kind === 'location' &&
      s.score >= EXACT_SCORE &&
      createSeriesAtFields(item, s.canonical_uid) !== null,
  )
}

/** One row's quick decide as a decide body. */
export interface QuickDecide {
  body: MappingDecideBody
  /** Past-tense phrase for the run log, e.g. "merged into X". */
  verb: string
  /** A rotating-series create (fields.series === true) folds the sibling
   *  rows backend-side — the board needs one refetch, not a row patch. */
  foldsSiblings: boolean
}

/** The row's single quick decide by priority: attach-target 100% >
 *  series-add 100% > create-series-at 100% > create-at 100% (attach/merge
 *  is the stronger claim — "this IS that existing thing"). null = no quick
 *  action (no 100% hint, a sighted convert, or a rotation row with neither
 *  a family nor a location hint — the series toggle must be seen). */
export function quickDecideFor(system: AccessSystem, item: MappingItem): QuickDecide | null {
  const base = { kind: item.kind, vendor_uid: item.vendor_uid }

  const attach = exactAttachHints(system, item)[0]
  if (attach) {
    const merge = isMergeTarget(attachTarget(system.provider, item.kind) as AttachTargetKind)
    const action = item.kind === 'new_credential' ? 'attach' : merge ? 'merge' : 'attach'
    return {
      body: { ...base, action, target: attach.canonical_uid },
      verb: `${action === 'merge' ? 'merged into' : 'attached to'} ${attach.display || attach.canonical_uid}`,
      foldsSiblings: false,
    }
  }
  if (isSightedCandidate(item)) return null

  const add = exactSeriesAddHints(item)[0]
  if (add) {
    return {
      body: { ...base, action: 'create', fields: { series: add.canonical_uid } },
      verb: `added to ${add.display || add.canonical_uid}`,
      foldsSiblings: false,
    }
  }
  const seriesAt = exactSeriesAtHints(item)[0]
  if (seriesAt) {
    return {
      body: { ...base, action: 'create', fields: createSeriesAtFields(item, seriesAt.canonical_uid) },
      verb: `series created at ${seriesAt.display || seriesAt.canonical_uid}`,
      foldsSiblings: true,
    }
  }
  const at = exactLocationHints(system, item)[0]
  if (at) {
    const fields = createAtFields(system, item, at.canonical_uid)
    return {
      body: { ...base, action: 'create', fields: fields && Object.keys(fields).length > 0 ? fields : null },
      verb: `created at ${at.display || at.canonical_uid}`,
      foldsSiblings: false,
    }
  }
  return null
}
